type OneOrMany<T> = T | T[];

/** Equality/inclusion over a context, plus a negation bucket. */
export type SelectorOn<T> = { [K in keyof T]?: OneOrMany<T[K]> } & { not?: { [K in keyof T]?: OneOrMany<T[K]> } };

/**
 * A selector may constrain any context axis EXCEPT the ones that identify the variant.
 *
 * This is the isolation requirement expressed as a type: a cross-variant rule is unrepresentable
 * because there is nowhere to put the variant axis. Each feature names its own excluded axes, so
 * the guarantee holds for country x flow, for transit group, or for whatever comes next - without
 * a comment explaining why a field is missing.
 */
export type Selector<TCtx, TExcluded extends keyof TCtx> = SelectorOn<Omit<TCtx, TExcluded>>;

const test = (want: unknown, got: unknown): boolean => (Array.isArray(want) ? want.includes(got) : want === got);

export const matches = <T extends object>(selector: SelectorOn<T> | undefined, ctx: T): boolean => {
  if (!selector) return true;

  for (const [key, want] of Object.entries(selector)) {
    if (key === 'not' || want === undefined) continue;
    if (!test(want, (ctx as Record<string, unknown>)[key])) return false;
  }

  for (const [key, want] of Object.entries((selector as { not?: Record<string, unknown> }).not ?? {})) {
    if (want === undefined) continue;
    if (test(want, (ctx as Record<string, unknown>)[key])) return false;
  }

  return true;
};
